import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  SpreadsheetImportSheet,
  type ImportTarget,
} from '@/components/requirement-import/spreadsheet-import-sheet';
import { adoptStaleDraft, afterDraftWrite, conflictReason } from '@/lib/api/requirement-library';
import {
  LibraryImportResultSchema,
  confirmLibraryImport,
  confirmedResultFromError,
  previewLibraryImport,
  uploadLibraryImport,
  type LibraryImportResult,
} from '@/lib/api/requirement-spreadsheets';
import { plural } from './library-logic';

/**
 * Spreadsheet intake into a module's draft (T-0153; E11-F03 scenarios 3
 * to 5). Curators only, from edit mode. The review surface is the shared
 * `SpreadsheetImportSheet`; this file supplies the library's requests and
 * wording.
 *
 * What differs from the engagement import: the library is neutral, so a
 * criticality column is ignored and the review says so (amendment 01,
 * Decision 31); likely duplicates are checked against the whole library;
 * a row whose text is already in this module is refused and listed; the
 * rows land in the draft as additions, unpublished; the confirm carries
 * the draft's `base_revision` like every other draft write; and the
 * original file is not retained.
 */

/** The toast and the "imported" line, from the server's own result. */
export function libraryImportMessage(result: LibraryImportResult): string {
  const range =
    result.first_number && result.last_number
      ? result.first_number === result.last_number
        ? ` (${result.first_number})`
        : ` (${result.first_number} to ${result.last_number})`
      : '';
  const left: string[] = [];
  if (result.refused.length > 0) {
    left.push(`${result.refused.length.toLocaleString()} already in this module`);
  }
  if (result.duplicates_skipped > 0) {
    left.push(`${plural(result.duplicates_skipped, 'duplicate')} skipped`);
  }
  if (result.excluded > 0) left.push(`${result.excluded.toLocaleString()} excluded`);
  return `${plural(result.added, 'requirement')} added to the draft of ${result.module_name}${range}${
    left.length > 0 ? `; ${left.join(', ')}` : ''
  }. Publish the module to make them available.`;
}

export interface LibraryImportSheetProps {
  moduleId: string;
  moduleName: string;
  /**
   * The screen's draft write queue: runs `fn` after the writes before it,
   * with the draft revision they left.
   */
  runWrite: <T>(moduleId: string, fn: (baseRevision: number) => Promise<T>) => Promise<T>;
  onClose: () => void;
  onImported: (result: LibraryImportResult) => void;
}

export function LibraryImportSheet({
  moduleId,
  moduleName,
  runWrite,
  onClose,
  onImported,
}: LibraryImportSheetProps) {
  const qc = useQueryClient();
  const target = React.useMemo<ImportTarget<LibraryImportResult>>(
    () => ({
      variant: 'library',
      title: 'Import a spreadsheet',
      description: `Into the draft of ${moduleName}. Rows stay unpublished until you publish the module.`,
      upload: (file, signal, progress) => uploadLibraryImport(moduleId, file, signal, progress),
      preview: (token, overrides, signal) =>
        previewLibraryImport(moduleId, token, overrides, signal),
      confirm: async (input) => {
        let result: LibraryImportResult;
        try {
          result = await runWrite(moduleId, (baseRevision) =>
            confirmLibraryImport(moduleId, { ...input, baseRevision }),
          );
        } catch (err) {
          // Confirmed already (a retry after a lost response): that result stands.
          const earlier = confirmedResultFromError(err, LibraryImportResultSchema);
          if (!earlier) {
            adoptStaleDraft(qc, moduleId, err);
            throw err;
          }
          result = earlier;
        }
        afterDraftWrite(qc, moduleId, result.draft_revision);
        return result;
      },
      confirmLabel: (count) =>
        count > 0 ? `Add ${plural(count, 'requirement')} to the draft` : 'Nothing to add',
      describeResult: libraryImportMessage,
      describeError: (err) =>
        conflictReason(err) === 'stale_revision'
          ? 'This draft was changed by another curator. It has been reloaded: confirm again to add these rows to it.'
          : null,
    }),
    [moduleId, moduleName, runWrite, qc],
  );
  return <SpreadsheetImportSheet target={target} onClose={onClose} onImported={onImported} />;
}
